import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineChevronUpDown,
  HiOutlineInbox,
  HiOutlineMagnifyingGlass,
} from 'react-icons/hi2';
import { TableSkeleton } from './Skeletons';

const PAGE_SIZES = [10, 25, 50];

function compareValues(a, b) {
  const left = a ?? '';
  const right = b ?? '';
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (left !== '' && right !== '' && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
}

function pageWindow(current, total) {
  const pages = [];
  const push = (value) => {
    if (!pages.includes(value)) pages.push(value);
  };
  push(1);
  for (let page = current - 1; page <= current + 1; page += 1) {
    if (page > 1 && page < total) push(page);
  }
  if (total > 1) push(total);
  return pages;
}

export default function DataTable({
  columns,
  rows,
  loading = false,
  empty = 'Nothing to show',
  searchPlaceholder = 'Search',
  filterKey = '',
  rowKey,
  initialPageSize = 10,
  framed = true,
  className = '',
}) {
  const reduce = useReducedMotion();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState({ key: '', dir: 'asc' });

  useEffect(() => {
    setPage(1);
  }, [query, pageSize, filterKey]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = !needle
      ? rows
      : rows.filter((row) =>
          columns.some((column) => {
            const value = column.search ? column.search(row) : row[column.key];
            return String(value ?? '').toLowerCase().includes(needle);
          })
        );
    if (!sort.key) return matched;
    const column = columns.find((item) => item.key === sort.key);
    if (!column?.sort) return matched;
    const next = [...matched];
    next.sort((a, b) => {
      const result = compareValues(column.sort(a), column.sort(b));
      return sort.dir === 'asc' ? result : -result;
    });
    return next;
  }, [rows, query, columns, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;
  const visible = filtered.slice(start, start + pageSize);
  const from = filtered.length ? start + 1 : 0;
  const to = Math.min(start + pageSize, filtered.length);

  function toggleSort(column) {
    if (!column.sort) return;
    setSort((prev) => {
      if (prev.key !== column.key) return { key: column.key, dir: 'asc' };
      return { key: column.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }

  if (loading) {
    return <TableSkeleton columns={columns.length} />;
  }

  const cardClass = `${framed ? 'table-card panel' : 'table-card table-card--plain'} ${className}`.trim();

  return (
    <div className={cardClass}>
      <div className="table-toolbar">
        <label className="table-search">
          <HiOutlineMagnifyingGlass aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault();
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
        </label>
        <label className="table-pagesize">
          Rows
          <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} aria-label="Rows per page">
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>
                  {column.sort ? (
                    <button
                      type="button"
                      className={`sort-btn${sort.key === column.key ? ' is-sorted' : ''}`}
                      onClick={() => toggleSort(column)}
                    >
                      {column.label}
                      <HiOutlineChevronUpDown aria-hidden="true" />
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <motion.tbody
            key={`${currentPage}-${query}-${sort.key}-${sort.dir}-${filterKey}`}
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.22 }}
          >
            {visible.map((row, index) => (
              <tr key={rowKey ? rowKey(row, index) : row.id ?? index}>
                {columns.map((column) => (
                  <td key={column.key} className={column.className}>
                    {column.render ? column.render(row) : row[column.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
            {!visible.length ? (
              <tr>
                <td colSpan={columns.length} className="empty">
                  <HiOutlineInbox aria-hidden="true" />
                  <span>{query.trim() ? 'No matching rows' : empty}</span>
                </td>
              </tr>
            ) : null}
          </motion.tbody>
        </table>
      </div>
      <div className="table-footer">
        <p>
          Showing {from}–{to} of {filtered.length}
        </p>
        <div className="pager">
          <button type="button" className="page-btn" onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1} aria-label="Previous page">
            <HiOutlineChevronLeft />
          </button>
          {pageWindow(currentPage, pageCount).map((item, index, list) => {
            const previous = list[index - 1];
            const gap = previous && item - previous > 1;
            return (
              <React.Fragment key={item}>
                {gap ? <span className="pager__gap">…</span> : null}
                <button
                  type="button"
                  className={`page-btn${item === currentPage ? ' is-current' : ''}`}
                  onClick={() => setPage(item)}
                  aria-current={item === currentPage ? 'page' : undefined}
                >
                  {item}
                </button>
              </React.Fragment>
            );
          })}
          <button type="button" className="page-btn" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pageCount} aria-label="Next page">
            <HiOutlineChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}
